import { Button, type StackProps, Text } from "@chakra-ui/react"
import { type FC, useMemo } from "react"
import Link from "@/common/components/Link"
import { ROUTES } from "@/common/constants/routes"
import { useDevice } from "@/common/hooks/useDevice"
import {
  FeatureFlag,
  useOrganization,
} from "@/organization/providers/OrganizationProvider"
import NavigationTabs from "@/ui/components/NavigationTabs"
import { useMe } from "@/user/providers/MeProvider"
import { useIsWhiteLabel } from "@/whitelabel/hooks/useIsWhiteLabel"
import { getWhiteLabelRoute } from "@/whitelabel/utils/breadcrumb"

type Props = {
  slug: string
}
const GovernanceProposalsNavTabs: FC<Props & StackProps> = ({
  slug,
  ...stackProps
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  const { isFeatureFlagOn } = useOrganization()
  const isWhiteLabel = useIsWhiteLabel()

  const me = useMe()

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome-migration
  const tabs = useMemo(() => {
    const draftsTabs = me
      ? [
          {
            label: "My Drafts",
            url: isWhiteLabel
              ? getWhiteLabelRoute(ROUTES.governance.drafts.list(slug))
              : ROUTES.governance.drafts.list(slug),
          },
        ]
      : []

    const tabs = [
      {
        label: "Onchain",
        url: isWhiteLabel
          ? getWhiteLabelRoute(ROUTES.governance.proposals.list(slug))
          : ROUTES.governance.proposals.list(slug),
      },
      ...draftsTabs,
    ]

    return tabs
  }, [me?.address, slug, isWhiteLabel])

  const isLegacyLayoutEnabled = isFeatureFlagOn(FeatureFlag.LegacyLayout)

  if (isLegacyLayoutEnabled) {
    return null
  }

  return (
    <NavigationTabs
      mb="-4px"
      tabs={tabs}
      topRight={
        <Button
          as={Link}
          className="no-underline"
          fontSize="md"
          href={ROUTES.governance.proposal.create(slug)}
          variant="primary"
          width={{ base: "100%", md: "auto" }}
        >
          <Text display={onLargeDevice}>+ New proposal</Text>
          <Text display={onLittleDevice}>New</Text>
        </Button>
      }
      zIndex={600}
      {...stackProps}
    />
  )
}

export default GovernanceProposalsNavTabs
